'use client';

import { useState } from 'react';

import cx from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';

import { type Masked } from '~/contentful/graphql';
import { useBreakpoint } from '~/ui/components/grid/useBreakpoint';
import { Breakpoint } from '~/ui/styles/grid';

import styles from './accordion.module.scss';
import { AccordionCard } from './card';
import {
  ANIMATE_STATE_MD,
  ANIMATE_STATE_SM,
  ANIMATION_DURATION,
  DEFAULT_WIDTH,
  EASE_IN_OUT,
  EXIT_STATE_MD,
  EXIT_STATE_SM,
  EXPANDED_CONTENT_WIDTH,
  EXPANDED_CONTENT_WIDTH_PERCENTAGE,
} from './constants';
import { type GrantStoryAccordionCard_GrantActivityArticleFragment } from './query';
import { AccordionTrigger } from './trigger';

export interface GrantStoryAccordionProps {
  accordionCards: Masked<typeof GrantStoryAccordionCard_GrantActivityArticleFragment>[];
  className?: string;
}

export function GrantStoryAccordion({ accordionCards, className }: GrantStoryAccordionProps) {
  const isSmallBreakpoint = useBreakpoint(Breakpoint.MOBILE);
  const [disabledTrigger, setDisabledTrigger] = useState(false);

  const [expandedIndex, setExpandedIndex] = useState(0);

  const onActionClick = (index: number) => {
    setExpandedIndex(index);
    setDisabledTrigger(true);
  };

  const expandedItemWidth = isSmallBreakpoint ? DEFAULT_WIDTH : EXPANDED_CONTENT_WIDTH_PERCENTAGE;

  return (
    <div className={cx(styles.accordion, className)}>
      {!isSmallBreakpoint && (
        <div
          className={styles.placeholderCard}
          style={{
            width: expandedItemWidth,
          }}
        />
      )}

      <div className={styles.accordionInner}>
        {accordionCards.map((item, index) => {
          const isExpanded = expandedIndex === index;
          const itemWidth = isSmallBreakpoint
            ? DEFAULT_WIDTH
            : isExpanded
              ? EXPANDED_CONTENT_WIDTH_PERCENTAGE
              : `${(100 - EXPANDED_CONTENT_WIDTH) / (accordionCards.length - 1)}%`;

          return (
            <motion.div
              initial={{
                width: itemWidth,
              }}
              animate={{
                width: itemWidth,
              }}
              transition={{ duration: ANIMATION_DURATION, ease: EASE_IN_OUT }}
              className={cx(styles.accordionItem, styles[`accordionItem-light`])}
              key={index}
            >
              <div className={cx(styles.colorUnderlay, styles[`colorUnderlay-${index}`])} />

              <AnimatePresence initial={false}>
                {!isExpanded && (
                  <motion.div
                    initial={isSmallBreakpoint ? EXIT_STATE_SM : EXIT_STATE_MD}
                    animate={isSmallBreakpoint ? ANIMATE_STATE_SM : ANIMATE_STATE_MD}
                    transition={{
                      duration: ANIMATION_DURATION,
                      ease: EASE_IN_OUT,
                    }}
                    exit={isSmallBreakpoint ? EXIT_STATE_SM : EXIT_STATE_MD}
                    className={styles.buttonWrapper}
                    style={{ width: itemWidth }}
                    onAnimationComplete={() => setDisabledTrigger(false)}
                  >
                    <AccordionTrigger
                      disabled={disabledTrigger}
                      onClick={() => onActionClick(index)}
                      data={item}
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence initial={false}>
                {isExpanded && (
                  <motion.div
                    className={styles.contentWrapper}
                    style={{
                      width: expandedItemWidth,
                    }}
                    initial={isSmallBreakpoint ? EXIT_STATE_SM : EXIT_STATE_MD}
                    animate={isSmallBreakpoint ? ANIMATE_STATE_SM : ANIMATE_STATE_MD}
                    transition={{ duration: ANIMATION_DURATION, ease: EASE_IN_OUT }}
                    exit={isSmallBreakpoint ? EXIT_STATE_SM : EXIT_STATE_MD}
                  >
                    <AccordionCard data={item} />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
